import styled, { css } from 'styled-components';
import UnstyledImage from 'gatsby-image';

import colors from '@utils/colors';
import fonts, { weights } from '@utils/fonts';
import { mq } from '@utils/responsive';
import { Body, Heading } from '@components/type';

export const Wrapper = styled.section``;

export const Layout = styled.div`
  width: 100%;
  max-width: 990px;
  margin: 0 auto;
`;

export const Intro = styled.header`
  max-width: 820px;
  margin: 0 0 48px;
  text-align: left;
`;

export const SectionHeading = styled(Heading)`
  margin-bottom: 16px;
  color: ${colors.B500};
  text-transform: uppercase;
`;

export const Description = styled(Body)`
  margin: 0;
`;

export const Chart = styled.div`
  overflow: visible;
`;

export const Grade = styled.article`
  margin-bottom: 72px;

  &:last-child {
    margin-bottom: 0;
  }

  ${mq.gtsm`
    margin-bottom: 50px;
  `}
`;

export const GradeHeading = styled(Heading)`
  margin: 0;
  padding: 0 0 18px 65px;
  color: ${colors.B500};
  text-transform: uppercase;

  ${mq.gtsm`
    padding-left: 92px;
  `}

  ${mq.gtmd`
    padding: 0 0 24px 100px;
    font-size: 48px;
  `}
`;

export const GradeBody = styled.div`
  position: relative;
`;

export const Band = styled.div`
  position: relative;
  display: grid;
  min-height: 142px;
  grid-template-columns: 48px minmax(0, 1fr);
  background-color: ${(props) => props.$background};
  color: ${colors.N0};

  ${mq.gtsm`
    min-height: 178px;
    grid-template-columns: 72px minmax(0, 1fr);
  `}
`;

export const VerticalLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  border-right: 1px solid rgba(255, 255, 255, 0.28);
  font-family: ${(props) => fonts(props.theme.locale).SANS_CONDENSED};
  font-size: 18px;
  font-weight: ${weights.BOLD};
  letter-spacing: 0.02em;
  line-height: 1;
  text-transform: uppercase;
  writing-mode: vertical-rl;
  transform: rotate(180deg);

  ${mq.gtsm`
    font-size: 28px;
  `}
`;

export const Content = styled.div`
  align-self: center;
  margin: 0;
  padding: 12px 155px 12px 12px;

  p {
    margin: 0;
  }

  ul {
    margin: 0;
    padding: 0;
    list-style: none;
  }

  ${mq.gtsm`
    padding: 34px 240px 34px 20px;
  `}

  ${mq.gtmd`
    padding-right: 240px;
    padding-left: 28px;
  `}

  li {
    position: relative;
    margin-bottom: 8px;
    padding-left: 20px;
    font-family: ${(props) => fonts(props.theme.locale).SANS_CONDENSED};
    font-size: 16px;
    font-weight: ${weights.BOLD};
    line-height: 1.25;

    &:last-child {
      margin-bottom: 0;
    }

    &::before {
      position: absolute;
      top: 0.48em;
      left: 0;
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: currentColor;
      content: '';
    }

    ${mq.gtsm`
      margin-bottom: 12px;
      padding-left: 28px;
      font-size: 22px;

      &::before {
        width: 8px;
        height: 8px;
      }
    `}

    @media (min-width: 600px) and (max-width: 899px) {
      margin-bottom: 8px;
      font-size: 18px;
      line-height: 1.2;
    }
  }
`;

const largePhoto = css`
  width: 173px;
  height: 173px;
  z-index: 2;

  ${mq.gtmd`
    width: 288px;
    height: 288px;
  `}
`;

const smallPhoto = css`
  width: 129px;
  height: 129px;
  z-index: 1;

  ${mq.gtsm`
    width: 213px;
    height: 213px;
  `}
`;

export const Photo = styled.div`
  position: absolute;
  overflow: hidden;
  border-radius: 50%;
  background: ${colors.N100};
`;

export const PhotoImage = styled(UnstyledImage)`
  width: 100%;
  height: 100%;
`;

const actionsPhotoLarge = css`
  ${Photo}[data-role='actions'] {
    ${largePhoto};
  }

  ${Photo}[data-role='outcomes'] {
    ${smallPhoto};
  }
`;

const actionsPhotoSmall = css`
  ${Photo}[data-role='actions'] {
    ${smallPhoto};
  }

  ${Photo}[data-role='outcomes'] {
    ${largePhoto};
  }
`;

const photosRight = css`
  right: 0;

  ${Photo}[data-role='actions'] {
    right: -20px;

    ${mq.gtsm`
      right: -23px;
    `}
  }

  ${Photo}[data-role='outcomes'] {
    right: -22px;

    ${mq.gtmd`
      right: 62px;
    `}
  }
`;

const photosLeft = css`
  right: 0;

  ${Photo}[data-role='actions'] {
    right: -25px;

    ${mq.gtmd`
      right: 62px;
    `}
  }

  ${Photo}[data-role='outcomes'] {
    right: -23px;

    ${mq.gtmd`
      right: -24px;
    `}
  }
`;

export const Photos = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 160px;
  pointer-events: none;

  ${(props) => props.$imageSize === 'small' ? actionsPhotoSmall : actionsPhotoLarge};
  ${(props) => props.$imageSide === 'left' ? photosLeft : photosRight};

  ${Photo}[data-role='actions'] {
    top: ${(props) => props.$imageSide === 'left' ? (props.$imageSize === 'large' ? '-4px' : '-8px') : '-60px'};
    z-index: ${(props) => props.$imageLayer === 'front' ? 2 : 1};

    ${mq.gtsm`
      top: ${(props) => props.$imageSide === 'left' ? (props.$imageSize === 'large' ? '-12px' : '-28px') : '-65px'};
    `}
  }

  ${Photo}[data-role='outcomes'] {
    bottom: ${(props) => props.$imageSize === 'large' && props.$imageSide === 'left' ? '-29px' : '-28px'};
    z-index: ${(props) => props.$imageLayer === 'front' ? 1 : 2};

    ${mq.gtsm`
      bottom: ${(props) => props.$imageSize === 'large' && props.$imageSide === 'left' ? '-38px' : '-64px'};
    `}
  }

  ${mq.gtsm`
    width: 330px;
  `}

  ${mq.ltsm`
    right: 0;
    left: auto;
    width: 160px;

    ${Photo}[data-role='actions'],
    ${Photo}[data-role='outcomes'] {
      left: auto;
    }
  `}
`;
